import { Box, InputAdornment, Drawer, Typography, IconButton } from '@mui/material';
import Input from 'components/Common/Input';
import { useForm } from 'react-hook-form';
import Button from 'components/Common/Button';
import { useNavigate, useParams } from 'react-router-dom';
import { IoClose } from 'react-icons/io5';
import Grid from '@mui/material/Grid2';
import { useCreateOrUpdateDataSet, useGetDataSet } from 'apis/business/setting/knowledgeBase/hook';
import WebUrlFormSkeleton from './WebUrlFormSkeleton';

import Radio from 'components/Common/Radio';
import { normalizeWebsiteUrl } from '@/utils/helperFunctions';

export default function WebUrlForm() {
  const navigate = useNavigate();
  const { datasetId } = useParams();

  const { data: dataset, isLoading: isDataSetLoading } = useGetDataSet();
  const { mutateAsync: handleDataSet, isPending } = useCreateOrUpdateDataSet();

  const { handleSubmit, control } = useForm<any>({
    defaultValues: {
      datasource_type: 'website',
      configuration: {
        scrap_website: true
      }
    },
    values: dataset
      ? {
          ...dataset,
          configuration: {
            ...dataset.configuration,
            scrap_website: dataset.configuration?.scrap_website ?? true
          }
        }
      : {
          name: '',
          datasource_type: 'website',
          configuration: {
            scrap_website: true
          }
        }
    // resolver: zodResolver(DataSetSchema)
  });

  const onSubmit = (data: any) => {
    if (data.configuration.url) {
      data.configuration.url = normalizeWebsiteUrl(data.configuration.url);
    }
    handleDataSet(data);
  };

  if (isDataSetLoading) {
    return <WebUrlFormSkeleton />;
  }

  return (
    <Drawer
      anchor='right'
      open={true}
      onClose={() => navigate(-1)}
      sx={{
        '& .MuiDrawer-paper': {
          width: { xs: '100%', sm: 400, md: 600 },
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          zIndex: 100001
        }
      }}
    >
      {/* Fixed Header */}
      <Box
        sx={{
          px: 3,
          py: 2,
          borderBottom: '1px solid #DEDEDE',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0
        }}
      >
        <Typography variant='h6'>Configuration</Typography>
        <IconButton onClick={() => navigate(-1)} size='small'>
          <IoClose size={24} />
        </IconButton>
      </Box>

      {/* Form with Fixed Footer */}
      <Box
        component='form'
        onSubmit={handleSubmit(onSubmit)}
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* Scrollable Content */}
        <Box
          sx={{
            flex: 1,
            overflow: 'auto'
          }}
        >
          <Grid container padding={3}>
            <Grid size={12}>
              <Input name='name' label='Name' control={control} fullWidth required sx={{ marginTop: 0.5 }} />
            </Grid>
            {!datasetId && (
              <>
                <Grid size={12}>
                  <Radio
                    name='configuration.scrap_website'
                    label=''
                    control={control}
                    options={[
                      { label: 'Scrap Website', value: true },
                      { label: 'Scrap Url', value: false }
                    ]}
                  />
                </Grid>
                <Grid size={12} mb={2}>
                  <Input
                    name='configuration.url'
                    label='Url'
                    control={control}
                    placeholder='example.com'
                    slotProps={{
                      input: {
                        startAdornment: <InputAdornment position='start'>https://</InputAdornment>
                      }
                    }}
                    fullWidth
                  />
                </Grid>
              </>
            )}
          </Grid>
        </Box>

        {/* Fixed Footer */}
        <Box
          sx={{
            px: 3,
            py: 2,
            borderTop: '1px solid #DEDEDE',
            display: 'flex',
            justifyContent: 'end',
            gap: 2,
            flexShrink: 0
          }}
        >
          <Button
            color='secondary'
            variant='outlined'
            sx={{ border: '1px solid #F3F3F3' }}
            onClick={() => navigate(-1)}
          >
            Cancel
          </Button>
          <Button type='submit' variant='contained' isPending={isPending}>
            {datasetId ? 'Update' : 'Create'}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
}
